/**
 * Floating Help Panel
 *
 * Non-blocking, compact help panel that:
 * - Floats in bottom-right corner
 * - Shows context-aware suggestions
 * - Has "Go there" navigation buttons
 * - Can be minimized/expanded
 * - Doesn't block UI interaction
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  HelpCircle,
  X,
  ChevronDown,
  ChevronUp,
  ArrowRight,
  Sparkles,
  BookOpen,
  Keyboard,
  Target,
  ExternalLink,
  Lightbulb,
  PlayCircle,
  Clock,
  CheckCircle2,
  RotateCcw,
} from 'lucide-react';
import { useVisualHelp } from './VisualHelpProvider';
import { useGuidedExperience } from './GuidedExperienceProvider';
import { useTour } from '../tour';
import { usePipelineOptional } from '../../context/PipelineContext';
import { helpAPI, tourAPI } from '../../../api';

// Human labels for sub-tour IDs (extend as new sub-tours are seeded)
const TOUR_LABELS = {
  main: 'Visite globale',
  global_search: 'Recherche dynamique (Ctrl+K)',
  navigation: 'Navigation principale',
  dashboard_tour: 'Tableau de bord',
  pipeline: 'Pipeline du jour',
  insurance_contract: 'Contrat assurance',
  claim_declaration: 'Déclarer un sinistre',
  claim_manager: 'Gestionnaire sinistre',
  calendar: 'Calendrier',
  reports: 'Rapports',
  bulk_actions: 'Actions groupées',
  refuel: 'Ravitailler un véhicule',
  team_create: 'Créer une équipe',
  tools: 'Outillage',
  telematics: 'Télématique',
  fuel_cards: 'Cartes carburant',
  suppliers: 'Fournisseurs',
};

// Fallback quick actions (used if API fails or during initial load)
const FALLBACK_QUICK_ACTIONS = {
  dashboard: [
    { label: 'Ajouter un vehicule', nav_target: 'vehicles', action_type: 'add' },
    { label: 'Ajouter un conducteur', nav_target: 'drivers', action_type: 'add' },
  ],
};

export function FloatingHelpPanel() {
  const {
    isResourceCenterOpen,
    closeResourceCenter,
    openShortcuts,
    currentView,
    navigateTo,
  } = useVisualHelp();

  // Use TourProvider's startTour for actual tour functionality
  const { startTour } = useTour();
  const pipeline = usePipelineOptional();
  const pipelineEnabled = pipeline?.pipelineEnabled ?? false;

  const {
    workflows,
    userProgress,
    startWorkflow: startGuidedWorkflow,
    resetWorkflow,
  } = useGuidedExperience();

  const [isExpanded, setIsExpanded] = useState(true);
  const [articles, setArticles] = useState([]);
  const [activeArticle, setActiveArticle] = useState(null);
  const [quickActionsGrouped, setQuickActionsGrouped] = useState({});
  const [availableTours, setAvailableTours] = useState([]);

  // Fetch articles and quick actions on open, reset article state when panel closes
  useEffect(() => {
    if (isResourceCenterOpen) {
      // Fetch articles
      helpAPI
        .getArticles({ limit: 20 })
        .then(res => {
          if (res.ok && Array.isArray(res.data)) {
            setArticles(res.data);
          }
        })
        .catch(() => {});

      // Fetch quick actions
      helpAPI
        .getQuickActions()
        .then(res => {
          if (res.ok && res.data?.grouped) {
            setQuickActionsGrouped(res.data.grouped);
          }
        })
        .catch(() => {});

      // Fetch available tours
      tourAPI
        .getAvailable()
        .then(res => {
          if (res.ok && Array.isArray(res.data?.tours)) {
            setAvailableTours(res.data.tours);
          }
        })
        .catch(() => {});
    } else {
      // Reset article detail view when panel closes so it doesn't persist on next open
      setActiveArticle(null);
    }
  }, [isResourceCenterOpen]);

  // Get contextual articles for current view
  const contextualArticles = useMemo(() => {
    return articles.filter(a => a.related_view === currentView).slice(0, 3);
  }, [articles, currentView]);

  // Get quick actions for current view (from API or fallback)
  const quickActions = useMemo(() => {
    const apiActions = quickActionsGrouped[currentView] || quickActionsGrouped.dashboard || [];
    if (apiActions.length > 0) {
      return apiActions;
    }
    // Fall back to hardcoded if API hasn't loaded yet
    return FALLBACK_QUICK_ACTIONS[currentView] || FALLBACK_QUICK_ACTIONS.dashboard || [];
  }, [quickActionsGrouped, currentView]);

  // Get relevant guided workflows for current view (not completed)
  const relevantWorkflows = useMemo(() => {
    return workflows
      .filter(w => w.target_view === currentView || w.is_featured)
      .filter(w => {
        // Filter out completed workflows
        const progress = userProgress[w.workflow_code];
        return !progress || progress.status !== 'completed';
      })
      .slice(0, 3);
  }, [workflows, currentView, userProgress]);

  // Get completed workflows that can be replayed
  const completedWorkflows = useMemo(() => {
    return workflows
      .filter(w => {
        const progress = userProgress[w.workflow_code];
        return progress && (progress.status === 'completed' || progress.status === 'skipped');
      })
      .slice(0, 5);
  }, [workflows, userProgress]);

  // Handle reset and replay
  const handleResetAndReplay = async workflowCode => {
    const res = await resetWorkflow(workflowCode);
    if (res.ok) {
      closeResourceCenter();
      startGuidedWorkflow(workflowCode);
    }
  };

  // Handle navigation with spotlight - uses context navigateTo
  const handleNavigate = target => {
    navigateTo(target);
  };

  // Handle article click - show detail and navigation
  const handleArticleClick = article => {
    setActiveArticle(article);
    helpAPI.trackArticleView(article.slug).catch(() => {});
  };

  // Handle "Go there" for article - uses nav_target from API
  // Returns to main help view after navigation (to show contextual help for new view)
  const handleGoThere = article => {
    const navTarget = article.nav_target || article.related_view;
    if (navTarget) {
      handleNavigate(navTarget);
      // Go back to main help view to show contextual help for the new view
      setActiveArticle(null);
    }
  };

  if (!isResourceCenterOpen) return null;

  return (
    <div
      className="fixed bottom-4 right-4 z-[80] w-80 max-h-[60vh] flex flex-col bg-slate-800 rounded-xl shadow-2xl border border-slate-700 overflow-hidden"
      style={{ pointerEvents: 'auto' }}
    >
      {/* Header - Always visible */}
      <div className="flex items-center justify-between px-4 py-3 bg-slate-800 border-b border-slate-700">
        <div className="flex items-center gap-2">
          <HelpCircle className="w-5 h-5 text-amber-400" />
          <span className="font-medium text-white">Aide</span>
          {currentView && (
            <span className="text-xs px-2 py-0.5 bg-slate-700 text-slate-300 rounded">
              {currentView}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-700 rounded transition-colors"
          >
            {isExpanded ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
          </button>
          <button
            onClick={closeResourceCenter}
            className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-700 rounded transition-colors"
          >
            <X size={16} />
          </button>
        </div>
      </div>

      {/* Expandable content */}
      {isExpanded && (
        <div className="flex-1 overflow-y-auto">
          {/* Active article detail */}
          {activeArticle ? (
            <div className="p-4">
              <button
                onClick={() => setActiveArticle(null)}
                className="text-xs text-amber-400 hover:text-amber-300 mb-2 flex items-center gap-1"
              >
                ← Retour
              </button>
              <h3 className="font-medium text-white mb-2">{activeArticle.title}</h3>
              <p className="text-sm text-slate-300 mb-4">{activeArticle.summary}</p>

              {/* Content preview */}
              <div className="text-xs text-slate-400 mb-4 max-h-32 overflow-y-auto">
                {activeArticle.content?.split('\n').slice(0, 5).join('\n')}
              </div>

              {/* GO THERE button - the key feature - uses nav_target from API */}
              {(activeArticle.nav_target || activeArticle.related_view) && (
                <button
                  onClick={() => handleGoThere(activeArticle)}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-amber-500 hover:bg-amber-400 text-slate-900 font-medium rounded-lg transition-colors"
                >
                  <Target size={16} />
                  Aller a {activeArticle.nav_target || activeArticle.related_view}
                  <ArrowRight size={16} />
                </button>
              )}
            </div>
          ) : (
            <>
              {/* Quick Actions for current view */}
              <div className="p-3 border-b border-slate-700">
                <div className="text-xs font-medium text-slate-400 uppercase tracking-wide mb-2">
                  Actions rapides
                </div>
                <div className="space-y-1">
                  {quickActions.slice(0, 3).map((action, i) => (
                    <button
                      key={action.id || i}
                      onClick={() => {
                        if (action.nav_target) {
                          handleNavigate(action.nav_target);
                        }
                      }}
                      className="w-full flex items-center justify-between px-3 py-2 text-sm text-slate-300 hover:text-white hover:bg-slate-700/50 rounded-lg transition-colors text-left"
                    >
                      <span className="flex items-center gap-2">
                        <Lightbulb size={14} className="text-amber-400" />
                        {action.label}
                      </span>
                      {action.nav_target && <ArrowRight size={14} className="text-slate-500" />}
                    </button>
                  ))}
                </div>
              </div>

              {/* Contextual help articles */}
              {contextualArticles.length > 0 && (
                <div className="p-3 border-b border-slate-700">
                  <div className="text-xs font-medium text-slate-400 uppercase tracking-wide mb-2">
                    Aide pour cette page
                  </div>
                  <div className="space-y-1">
                    {contextualArticles.map(article => (
                      <button
                        key={article.slug}
                        onClick={() => handleArticleClick(article)}
                        className="w-full flex items-center justify-between px-3 py-2 text-sm text-slate-300 hover:text-white hover:bg-slate-700/50 rounded-lg transition-colors text-left"
                      >
                        <span className="flex items-center gap-2">
                          <BookOpen size={14} className="text-slate-500" />
                          <span className="truncate">{article.title}</span>
                        </span>
                        <ArrowRight size={14} className="text-slate-500" />
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Guided workflows - Available */}
              {relevantWorkflows.length > 0 && (
                <div className="p-3 border-b border-slate-700">
                  <div className="text-xs font-medium text-slate-400 uppercase tracking-wide mb-2">
                    Guides pas a pas
                  </div>
                  <div className="space-y-1">
                    {relevantWorkflows.map(workflow => {
                      const progress = userProgress[workflow.workflow_code];
                      const isInProgress = progress?.status === 'in_progress';

                      return (
                        <button
                          key={workflow.workflow_code}
                          onClick={() => {
                            closeResourceCenter();
                            startGuidedWorkflow(workflow.workflow_code);
                          }}
                          className="w-full flex items-center justify-between px-3 py-2 text-sm text-slate-300 hover:text-white hover:bg-slate-700/50 rounded-lg transition-colors text-left group"
                        >
                          <span className="flex items-center gap-2">
                            <PlayCircle
                              size={14}
                              className={isInProgress ? 'text-amber-400' : 'text-emerald-400'}
                            />
                            <span className="truncate">{workflow.title}</span>
                          </span>
                          <span className="flex items-center gap-2">
                            {workflow.estimated_minutes && (
                              <span className="flex items-center gap-1 text-xs text-slate-500">
                                <Clock size={10} />
                                {workflow.estimated_minutes}m
                              </span>
                            )}
                            {isInProgress && (
                              <span className="text-xs px-1.5 py-0.5 bg-amber-500/20 text-amber-400 rounded">
                                En cours
                              </span>
                            )}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Completed/Skipped workflows - Can be replayed */}
              {completedWorkflows.length > 0 && (
                <div className="p-3 border-b border-slate-700">
                  <div className="text-xs font-medium text-slate-400 uppercase tracking-wide mb-2">
                    Guides termines
                  </div>
                  <div className="space-y-1">
                    {completedWorkflows.map(workflow => {
                      const progress = userProgress[workflow.workflow_code];
                      const isCompleted = progress?.status === 'completed';

                      return (
                        <button
                          key={workflow.workflow_code}
                          onClick={() => handleResetAndReplay(workflow.workflow_code)}
                          className="w-full flex items-center justify-between px-3 py-2 text-sm text-slate-400 hover:text-white hover:bg-slate-700/50 rounded-lg transition-colors text-left group"
                        >
                          <span className="flex items-center gap-2">
                            {isCompleted ? (
                              <CheckCircle2 size={14} className="text-emerald-500" />
                            ) : (
                              <RotateCcw size={14} className="text-slate-500" />
                            )}
                            <span className="truncate">{workflow.title}</span>
                          </span>
                          <span className="flex items-center gap-1 text-xs text-slate-500 group-hover:text-amber-400">
                            <RotateCcw size={10} />
                            Rejouer
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Visites guidées (main + sub-tours) */}
              {availableTours.length > 0 && (
                <div className="p-3 border-b border-slate-700">
                  <div className="text-xs font-medium text-slate-400 uppercase tracking-wide mb-2">
                    Visites guidées
                  </div>
                  <div className="space-y-1">
                    {availableTours
                      .filter(t => t.tourId !== 'pipeline' || pipelineEnabled)
                      .slice()
                      .sort((a, b) => (a.tourId === 'main' ? -1 : b.tourId === 'main' ? 1 : a.tourId.localeCompare(b.tourId)))
                      .map(t => (
                        <button
                          key={t.tourId}
                          onClick={() => {
                            closeResourceCenter();
                            startTour(t.tourId);
                          }}
                          className="w-full flex items-center justify-between px-3 py-2 text-sm text-slate-300 hover:text-white hover:bg-slate-700/50 rounded-lg transition-colors text-left"
                        >
                          <span className="flex items-center gap-2">
                            <PlayCircle
                              size={14}
                              className={t.tourId === 'main' ? 'text-amber-400' : 'text-emerald-400'}
                            />
                            <span className="truncate">{TOUR_LABELS[t.tourId] || t.tourId}</span>
                          </span>
                          <span className="text-xs text-slate-500">
                            {t.stepCount} étape{t.stepCount > 1 ? 's' : ''}
                          </span>
                        </button>
                      ))}
                  </div>
                </div>
              )}

              {/* Global actions */}
              <div className="p-3">
                <div className="space-y-1">
                  <button
                    onClick={() => {
                      closeResourceCenter();
                      openShortcuts();
                    }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-300 hover:text-white hover:bg-slate-700/50 rounded-lg transition-colors"
                  >
                    <Keyboard size={14} className="text-blue-400" />
                    Raccourcis clavier
                    <kbd className="ml-auto text-xs px-1.5 py-0.5 bg-slate-700 rounded">Ctrl+/</kbd>
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Minimized hint */}
      {!isExpanded && (
        <div className="px-4 py-2 text-xs text-slate-400">Cliquez pour developper l'aide</div>
      )}
    </div>
  );
}

export default FloatingHelpPanel;
